import React, { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AddBankAccountModal } from "@features/AddBankAccountModal/AddBankAccountModal";
import { Button } from "@components/Button";
import { ClassicLoader } from "@components/ClassicLoader/ClassicLoader";
import { Modal } from "@components/Modal/Modal";
import { useModal } from "@hooks/useModal";
import { useModalStore, type IUseModalProps } from "@store/modal/modal";
import { useAccountScoringEmbed } from "./useAccountScoringEmbed";

interface IAccountScoringEmbedModalProps extends IUseModalProps {
  invitationKey: number;
  clientId: string;
  locale: string;
}

export const AccountScoringEmbedModal: React.FC<
  IAccountScoringEmbedModalProps
> = (props) => {
  const { invitationKey, clientId, locale, ...rest } = props;
  const { initiateConsent, clearState } = useAccountScoringEmbed();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { create } = useModalStore();

  const modal = useModal({
    ...rest,
    onClose: () => {
      clearState();
      rest.onClose?.();
    },
  });

  const id = useId();
  const formattedId = "accountScoringEmbedModal" + id.replace(/:/g, "_");

  const [instanceCreated, setInstanceCreated] = useState(false);
  const [passedBankSelect, setPassedBankSelect] = useState(false);
  const [isFinished, setIsFinished] = useState(false);

  useEffect(() => {
    if (instanceCreated) {
      return;
    }

    void initiateConsent({
      containerId: formattedId,
      invitationKey,
      clientId,
      locale,
      onTransactionsFetched: () => {
        setIsFinished(true);
      },
    });

    setInstanceCreated(true);
  }, [
    clearState,
    clientId,
    formattedId,
    initiateConsent,
    instanceCreated,
    invitationKey,
    locale,
    modal,
    t,
  ]);

  // Search for button inside AS container and click it to prevent duplicate bank select
  useEffect(() => {
    if (!instanceCreated || passedBankSelect) {
      return;
    }

    const interval = setInterval(() => {
      try {
        const buttonContainer = document.getElementsByClassName(
          "asc-container-banks",
        );

        if (!buttonContainer[0]) {
          return;
        }

        buttonContainer[0].querySelector("button")?.click();

        setPassedBankSelect(true);
      } catch {
        setPassedBankSelect(true);
      }
    }, 10);

    // Prevent infinite search
    const timeout = setTimeout(() => {
      clearInterval(interval);
      setPassedBankSelect(true);
    }, 30_000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [instanceCreated, passedBankSelect]);

  const openAddBankAccountModal = () => {
    create((modalProps) => <AddBankAccountModal {...modalProps} />);
    modal.close();
  };

  const navigateToBudgetAndCloseModal = () => {
    navigate("/budget");
    modal.close();
  };

  return (
    <Modal
      modal={modal}
      className="md:max-w-[32rem]"
      actionButtons={
        isFinished && (
          <React.Fragment>
            <Button
              variant="secondary"
              className="w-full"
              onClick={navigateToBudgetAndCloseModal}
            >
              {t("common.form.buttons.cancel")}
            </Button>
            <Button className="w-full" onClick={openAddBankAccountModal}>
              {t("accountScoringEmbedModal.addOtherBanks")}
            </Button>
          </React.Fragment>
        )
      }
    >
      {!isFinished && (
        <div>
          <div
            id={formattedId}
            className="flex h-60 items-center justify-center"
          >
            <ClassicLoader />
          </div>
        </div>
      )}
      {isFinished && (
        <div className="flex flex-col items-center px-6 text-center">
          <img
            src="/images/budget/magnifying-glass-bills.png"
            className="size-24 object-cover"
          />
          <h2 className="mt-2 text-lg font-semibold">
            {t("accountScoringEmbedModal.success.title")}
          </h2>
          <span className="mt-4 text-sm text-grey-dark">
            {t("accountScoringEmbedModal.success.subtitle")}
          </span>
          <span className="mt-8 text-sm font-semibold">
            {t("accountScoringEmbedModal.success.subtitle2")}
          </span>
          <span className="text-sm text-grey-dark">
            {t("accountScoringEmbedModal.success.subtitle3")}
          </span>
        </div>
      )}
    </Modal>
  );
};
